import React from 'react';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import { useLocation } from 'react-router-dom';
import SpotlightTour from './SpotlightTour';

export const PROBLEM_SOLVE_TOUR_KEY = 'ch_problem_solve_tour_v3';
export const PROBLEM_SOLVE_TOUR_START_EVENT = 'ch-start-problem-solve-tour';
export const PROBLEM_SOLVE_TOUR_COMPLETE_EVENT = 'ch-problem-solve-tour-complete';

const steps = [
  {
    id: 'welcome',
    placement: 'center',
    eyebrow: 'Solve mode',
    title: 'Read, code, run, submit',
    body: 'Statement on the left, editor on the right. This quick tour shows the tabs, focus layout, and how to test your solution.',
    cta: 'Show me around',
  },
  {
    id: 'statement',
    target: '[data-tour="problem-statement"]',
    placement: 'right',
    eyebrow: 'Understand first',
    title: 'Description · Explanation · Submissions',
    body: 'Read the problem here. Switch tabs for a walkthrough (when available) or your past submissions on this problem.',
  },
  {
    id: 'hint',
    target: '[data-tour="problem-hint"]',
    placement: 'right',
    eyebrow: 'Stuck?',
    title: 'AI Hints',
    body: 'Scroll down on the Description tab for AI-generated hints — Basic, Medium, and Advanced — so you can reveal just as much help as you need.',
  },
  {
    id: 'editor',
    target: '[data-tour="problem-code"]',
    placement: 'left',
    eyebrow: 'Write code',
    title: 'Code editor',
    body: 'Write your solution here. Pick a language from the toolbar above — Java, Python, C++, JS, or C#.',
  },
  {
    id: 'actions',
    target: '[data-tour="problem-actions"]',
    placement: 'bottom',
    eyebrow: 'Test & ship',
    title: 'Run locally, then Submit',
    body: 'Run against the sample test case in the Test Cases tab. Results appear under Test Result. Shortcuts: Run ⌘\', Submit ⌘⏎ — see Settings for the full list.',
  },
  {
    id: 'timer',
    target: '[data-tour="problem-timer"]',
    placement: 'bottom',
    eyebrow: 'Pace yourself',
    title: 'Solve timer',
    body: 'Start a stopwatch or set a countdown (with alarm). Pause, reset, and tweak auto-start / auto-pause from the timer settings.',
  },
  {
    id: 'focus',
    target: '[data-tour="problem-focus-mode"]',
    placement: 'bottom',
    eyebrow: 'Stay in the zone',
    title: 'Focus mode',
    body: 'Keeps the page minimal — just the problem and editor. Toggle this icon anytime, or switch it in Editor Settings. Drag the divider to resize the panels.',
  },
  {
    id: 'finish',
    placement: 'center',
    eyebrow: "You're set",
    title: 'Time to solve',
    body: 'Replay this tour anytime with the help icon in the editor toolbar. Use focus mode when you want zero distractions. Good luck!',
    cta: 'Start coding',
  },
];

const ProblemSolveTour = () => {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  // /problems/:slug — not the list, not admin routes
  const active = parts[0] === 'problems' && parts.length === 2;

  return (
    <SpotlightTour
      steps={steps}
      storageKey={PROBLEM_SOLVE_TOUR_KEY}
      startEvent={PROBLEM_SOLVE_TOUR_START_EVENT}
      completeEvent={PROBLEM_SOLVE_TOUR_COMPLETE_EVENT}
      active={active}
      autoStartDelay={1100}
      isReady={() =>
        Boolean(
          document.querySelector('[data-tour="problem-statement"]') &&
            document.querySelector('[data-tour="problem-actions"]') &&
            document.querySelector('[data-tour="problem-code"]') &&
            document.querySelector('[data-tour="problem-focus-mode"]'),
        )
      }
      finishIcon={<CodeOutlinedIcon sx={{ fontSize: 14 }} />}
    />
  );
};

export default ProblemSolveTour;
